/**
 * DashboardPage - painel principal do cliente
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    CalendarClock,
    Eye,
    Heart,
    TrendingUp,
    Plus,
    Sparkles,
    Share2,
} from "lucide-react";
import KPICard from "@/components/shared/KPICard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import ChannelIcon from "@/components/shared/ChannelIcon";
import StatusBadge from "@/components/shared/StatusBadge";
import MarketplaceStrip from "@/components/app/MarketplaceStrip";
import { fmtDateTime, relativeFromNow } from "@/lib/date";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    Area,
    AreaChart,
} from "recharts";

const HOURS = ["06h", "09h", "12h", "15h", "18h", "21h"];

export default function DashboardPage() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [posts, setPosts] = useState([]);
    const [channels, setChannels] = useState([]);

    useEffect(() => {
        Promise.all([
            api.get("/reports/overview"),
            api.get("/posts"),
            api.get("/channels"),
        ]).then(([rep, p, ch]) => {
            setData(rep.data);
            setPosts(p.data);
            setChannels(ch.data);
        });
    }, []);

    const k = data?.kpi || {};
    const series = data?.series || [];
    const upcoming = posts
        .filter((p) => p.status === "agendado")
        .sort((a, b) => new Date(a.scheduled_for) - new Date(b.scheduled_for))
        .slice(0, 5);
    const recent = posts
        .filter((p) => p.status === "publicado")
        .slice(0, 5);
    const connectedChannels = channels.filter((c) => c.connected);

    return (
        <div className="space-y-6" data-testid="dashboard-page">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                        Olá, {user?.name?.split(" ")[0]} 👋
                    </h1>
                    <p className="text-muted-foreground">Aqui está o seu piloto automático em ação.</p>
                </div>
                <Button asChild className="btn-grad-purple text-white" data-testid="dashboard-new-publication-button">
                    <Link to="/publicacoes?new=1" className="gap-2">
                        <Plus className="h-4 w-4" /> Nova publicação
                    </Link>
                </Button>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard
                    icon={CalendarClock}
                    label="Agendadas"
                    value={k.total_scheduled ?? 0}
                    hint="a publicar em breve"
                    accent="#A78BFA"
                    testid="dashboard-kpi-scheduled"
                />
                <KPICard
                    icon={Eye}
                    label="Alcance (30d)"
                    value={(k.total_reach ?? 0).toLocaleString("pt-PT")}
                    hint={`${k.total_published ?? 0} publicadas`}
                    accent="#60A5FA"
                    testid="dashboard-kpi-reach"
                />
                <KPICard
                    icon={Heart}
                    label="Engagement"
                    value={(k.total_engagement ?? 0).toLocaleString("pt-PT")}
                    hint={`${k.avg_engagement_rate ?? 0}% taxa média`}
                    accent="#FB7185"
                    testid="dashboard-kpi-engagement"
                />
                <KPICard
                    icon={Share2}
                    label="Canais ligados"
                    value={`${k.connected_channels ?? 0}/6`}
                    hint="redes sociais"
                    accent="#2DD4BF"
                    testid="dashboard-kpi-channels"
                />
            </div>

            {/* Charts + Upcoming */}
            <div className="grid lg:grid-cols-3 gap-4">
                <Card className="glass-card lg:col-span-2 p-5 rounded-2xl" data-testid="dashboard-reach-chart">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="font-display text-lg font-semibold">Alcance · últimos 30 dias</h3>
                            <p className="text-xs text-muted-foreground">Soma diária por canal</p>
                        </div>
                        <Badge className="bg-[#6B21D9]/15 text-[#A78BFA] border-[#6B21D9]/30 border">
                            ao vivo <span className="ml-1 h-1.5 w-1.5 rounded-full bg-[#A78BFA] animate-soft-pulse" />
                        </Badge>
                    </div>
                    <div className="h-[260px]">
                        <ResponsiveContainer>
                            <AreaChart data={series} margin={{ left: -20, right: 6, top: 8 }}>
                                <defs>
                                    <linearGradient id="reachGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#A78BFA" stopOpacity={0.5} />
                                        <stop offset="100%" stopColor="#A78BFA" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="rgba(255,255,255,0.06)" />
                                <XAxis dataKey="date" stroke="#A7A9C0" fontSize={11} />
                                <YAxis stroke="#A7A9C0" fontSize={11} />
                                <Tooltip
                                    contentStyle={{
                                        background: "#141625",
                                        border: "1px solid rgba(255,255,255,0.1)",
                                        borderRadius: 12,
                                        color: "#EDEDF7",
                                    }}
                                />
                                <Area type="monotone" dataKey="reach" stroke="#A78BFA" fill="url(#reachGrad)" strokeWidth={2} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </Card>

                <Card className="glass-card p-5 rounded-2xl" data-testid="dashboard-upcoming">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-display text-lg font-semibold">Próximas publicações</h3>
                        <Button asChild variant="ghost" size="sm">
                            <Link to="/calendario">Ver tudo</Link>
                        </Button>
                    </div>
                    {upcoming.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-8 text-center">
                            Nada agendado. Crie a sua primeira publicação.
                        </div>
                    ) : (
                        <ul className="space-y-3">
                            {upcoming.map((p) => (
                                <li key={p.id} className="flex items-start gap-3 p-3 rounded-xl bg-white/3 border border-white/5">
                                    <div className="flex flex-col gap-1">
                                        {p.channels.slice(0, 2).map((ch) => (
                                            <ChannelIcon key={ch} type={ch} showBg className="h-3 w-3" />
                                        ))}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm line-clamp-2">{p.caption}</div>
                                        <div className="text-xs text-muted-foreground mt-1">
                                            <span className="text-[#A78BFA]">{relativeFromNow(p.scheduled_for)}</span>{" "}
                                            · {fmtDateTime(p.scheduled_for)}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
            </div>

            {/* Best hours + Recent posts */}
            <div className="grid lg:grid-cols-3 gap-4">
                <Card className="glass-card p-5 rounded-2xl" data-testid="dashboard-best-hours">
                    <h3 className="font-display text-lg font-semibold mb-1">Melhores horas para publicar</h3>
                    <p className="text-xs text-muted-foreground mb-4">Recomendação do nosso piloto automático</p>
                    <div className="space-y-2">
                        {HOURS.map((h, i) => {
                            const score = [40, 65, 75, 90, 80, 55][i];
                            return (
                                <div key={h} className="flex items-center gap-2">
                                    <span className="w-10 text-xs text-muted-foreground">{h}</span>
                                    <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
                                        <div
                                            className="h-full btn-grad-purple"
                                            style={{ width: `${score}%` }}
                                        />
                                    </div>
                                    <span className="w-10 text-right text-xs font-medium">{score}</span>
                                </div>
                            );
                        })}
                    </div>
                </Card>

                <Card className="glass-card lg:col-span-2 p-5 rounded-2xl" data-testid="dashboard-recent">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-display text-lg font-semibold">Atividade recente</h3>
                        <Button asChild variant="ghost" size="sm">
                            <Link to="/publicacoes">Ver todas</Link>
                        </Button>
                    </div>
                    {recent.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-8 text-center">
                            Sem publicações recentes.
                        </div>
                    ) : (
                        <ul className="divide-y divide-white/5">
                            {recent.map((p) => (
                                <li key={p.id} className="py-3 flex items-center gap-3">
                                    <div className="flex flex-col gap-1">
                                        {p.channels.slice(0, 2).map((ch) => (
                                            <ChannelIcon key={ch} type={ch} showBg className="h-3 w-3" />
                                        ))}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm line-clamp-1">{p.caption}</div>
                                        <div className="text-xs text-muted-foreground">
                                            {fmtDateTime(p.published_at)} · {p.metrics?.reach || 0} alcance
                                        </div>
                                    </div>
                                    <StatusBadge status="publicado" />
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
            </div>

            {/* Marketplace strip */}
            <MarketplaceStrip />

            {/* AI suggestion CTA */}
            <Card className="glass-card p-5 rounded-2xl flex items-center gap-4" data-testid="dashboard-ai-cta">
                <div className="h-12 w-12 rounded-xl btn-grad-purple grid place-items-center text-white">
                    <Sparkles className="h-5 w-5" />
                </div>
                <div className="flex-1">
                    <h4 className="font-display font-semibold">A IA tem ideias para o seu negócio.</h4>
                    <p className="text-sm text-muted-foreground">
                        Gere legendas, hashtags e imagens em segundos.
                    </p>
                </div>
                <Button asChild className="btn-grad-purple text-white">
                    <Link to="/publicacoes?new=1">Gerar agora</Link>
                </Button>
            </Card>
        </div>
    );
}
